import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowRight,
  Loader2,
  Coins,
  Users,
  Zap,
  Shield,
  Headphones,
  Building2,
  RefreshCw
} from 'lucide-react'
import { useAuthStore } from '@/store/authStore'
import { checkoutService, type CreditPack, type CreditBalance } from '@/services/checkout'
import toast from 'react-hot-toast'
import styles from './Plans.module.css'

export function Plans() {
  const [packs, setPacks] = useState<CreditPack[]>([])
  const [balance, setBalance] = useState<CreditBalance | null>(null)
  const [loading, setLoading] = useState(true)
  useAuthStore()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [packsData, balanceData] = await Promise.all([
        checkoutService.getPacks(),
        checkoutService.getBalance()
      ])
      setPacks(packsData)
      setBalance(balanceData)
    } catch (error) {
      toast.error('Failed to load credit packs')
    } finally {
      setLoading(false)
    }
  }

  const handleBuy = (packId: string) => {
    window.location.href = `/checkout?pack=${packId}`
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-bg">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 mx-auto mb-3 text-theme-primary animate-spin" />
          <span className="text-theme-secondary font-roboto-mono">LOADING CREDITS...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Hero */}
      <div className={`px-6 pt-8 pb-10 ${styles.heroGradient}`}>
        <div className="max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h1 className="ds-page-title mb-3">
              BUY CREDITS
            </h1>
            <p className="text-base mb-6 text-theme-text font-roboto-mono">
              Credits are used each time you run a course. Buy what you need.
            </p>

            {/* Current Balance */}
            {balance && (
              <div className="ds-card ds-card-prominent p-4 mb-6 max-w-sm mx-auto">
                <p className="text-xs uppercase tracking-wider mb-1 text-theme-text font-roboto-mono">Your Balance</p>
                <p className="text-3xl font-bold text-theme-primary font-orbitron text-shadow-primary">
                  {balance.credit_balance} <span className="text-base font-normal text-theme-text">credits</span>
                </p>
              </div>
            )}

            <div className="flex items-center justify-center gap-6 text-sm text-theme-text">
              <div className="flex items-center gap-1.5">
                <Users className="w-4 h-4" />
                <span>Up to 250 students per lesson</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Coins className="w-4 h-4" />
                <span>Pay as you go</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Zap className="w-4 h-4" />
                <span>Instant activation</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Credit Packs Grid */}
      <div className="max-w-5xl mx-auto px-6 py-10">
        <div className="grid md:grid-cols-3 gap-6">
          {packs.map((pack, index) => {
            const isBestValue = index === packs.length - 1

            return (
              <motion.div
                key={pack.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className={`relative rounded-xl overflow-hidden border transition-all ${styles.packCard}`}
                data-best-value={isBestValue}
              >
                {isBestValue && (
                  <div className={`text-center py-1.5 text-xs font-bold uppercase tracking-wider text-theme-on-primary ${styles.bestValueBanner}`}>
                    Best Value
                  </div>
                )}

                <div className="p-6">
                  <h3 className="text-xl font-bold mb-1 text-theme-secondary font-orbitron">{pack.name}</h3>
                  <p className="text-sm mb-5 text-theme-text">{pack.description}</p>

                  <div className="mb-5">
                    <div className="text-3xl font-bold text-theme-primary font-orbitron text-shadow-primary">
                      {checkoutService.formatPrice(pack.cost)}
                    </div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm font-semibold text-theme-secondary">
                        {pack.credits} credits
                      </span>
                      <span className="text-xs text-theme-text">
                        ({checkoutService.formatPrice(pack.cost / pack.credits)}/credit)
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={() => handleBuy(pack.id)}
                    className={`w-full py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-all ${styles.buyButton}`}
                    data-best-value={isBestValue}
                  >
                    Buy Credits
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )
          })}
        </div>

        {packs.length === 0 && !loading && (
          <div className="text-center py-16 text-theme-text font-roboto-mono">
            No credit packs available at this time.
          </div>
        )}

        {/* Additional Info */}
        <div className="mt-14">
          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {[
              { icon: Shield, title: 'Secure Payments', desc: 'All payments processed securely through Stripe' },
              { icon: Headphones, title: 'Support', desc: 'Get help when you need it from our team' },
              { icon: Building2, title: 'For Organizations', desc: 'Contact us for volume pricing for your organization' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="ds-card text-center p-5">
                <Icon className="w-10 h-10 mx-auto mb-3 text-theme-secondary" />
                <h3 className="font-semibold mb-1 text-theme-primary">{title}</h3>
                <p className="text-sm text-theme-text">{desc}</p>
              </div>
            ))}
          </div>

          <div className="ds-card p-6">
            <h3 className="ds-section-title mb-3">
              HOW CREDITS WORK
            </h3>
            <p className="text-sm mb-2 text-theme-text">
              Each course has <strong className="text-theme-primary">4 student tiers</strong> (25, 50, 100, and 250 students). When you create a run, you select a tier that fits your class size. The credit cost for each tier is set by the course creator and varies from course to course.
            </p>
            <p className="text-sm mb-2 text-theme-text">
              If enrollment exceeds your selected tier, an overage cost per additional student may apply, depending on the course.
            </p>
            <p className="text-sm text-theme-text">
              Credits never expire. Buy more anytime you need them.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
